import { Chats } from '@phosphor-icons/react'
import { useInfiniteQuery } from '@tanstack/react-query'
import React, { useContext, useEffect, useMemo } from 'react'

import ResourceCommentForm from '@/components/resource/comments/ResourceCommentForm'
import ResourceCommentItem from '@/components/resource/comments/ResourceCommentItem'
import InfiniteScroll from '@/components/shared/components/InfiniteLoaders'
import PostCommentLoader from '@/components/shared/components/PostCommentLoader'
import EmptyState from '@/components/shared/ui/EmptyState'
import { PAGINATION_LIMIT } from '@/config/appConstants'
import { queryClient } from '@/config/queryClient'
import HNContext from '@/context/HNContext'
import { useTranslations } from '@/hooks/useTranslations'
import type { IEntityUpdateEventData } from '@/lib/eventEmitter'
import { ENTITIES, EVENT_ACTION_TYPES, EventEmitter } from '@/lib/eventEmitter'
import {
  removeDuplicatesInArray,
  sortArrayOfObjects,
} from '@/lib/helpers/dataHelpers'
import type { IResources } from '@/models/Resource'
import { getResourceComments } from '@/models/Resource'
import type { IComment } from '@/types/comment'

interface IPropTypes {
  resourceId: string | number
  resourceModule: IResources
}
export default function ResourceCommentList({
  resourceId,
  resourceModule,
}: IPropTypes) {
  const t = useTranslations()
  const { userProfile } = useContext(HNContext)

  const {
    data: comments,
    isLoading: isLoadingFromDB,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['comments', resourceId, resourceModule],
    queryFn: ({ pageParam = 1 }) =>
      getResourceComments(resourceId, resourceModule, {
        page: pageParam,
        per_page: PAGINATION_LIMIT.comment,
      }).then((data) => {
        return {
          data,
          nextCursor:
            data.length >= PAGINATION_LIMIT.comment ? pageParam + 1 : null,
        }
      }),
    getNextPageParam: (lastPage, _pages) => lastPage.nextCursor,
    initialPageParam: 1,
  })

  const handleNewComment = (comment: IComment) => {
    queryClient.setQueryData(
      ['comments', resourceId, resourceModule],
      (oldData: { pages: { data: IComment[]; nextCursor: number }[] }) => {
        return {
          ...oldData,
          pages: [
            {
              ...oldData?.pages?.[0],
              data: [comment, ...(oldData?.pages?.[0]?.data || [])],
            },
            ...(oldData?.pages?.slice(1) || []),
          ],
        }
      }
    )
  }

  const handleDelete = (comment: IComment) => {
    queryClient.setQueryData(
      ['comments', resourceId, resourceModule],
      (oldData: { pages: { data: IComment[] }[] }) => {
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data
              .filter((c) => c.id !== comment.id)
              .map((c) => ({
                ...c,
                replies: c.replies.filter((r) => r.id !== comment.id),
              })),
          })),
        }
      }
    )
  }
  const handleUpdate = (comment: IComment) => {
    queryClient.setQueryData(
      ['comments', resourceId, resourceModule],
      (oldData: { pages: { data: IComment[] }[] }) => {
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.map((c) =>
              c.id === comment.id ? { ...c, ...comment } : c
            ),
          })),
        }
      }
    )
  }

  const handleCommentEvents = (eventData: IEntityUpdateEventData): any => {
    if (eventData.entity !== ENTITIES.COMMENT) return
    const incomingComment = eventData.data.data as IComment
    switch (eventData.actionType) {
      case EVENT_ACTION_TYPES.ADD:
        handleNewComment(incomingComment)
        break
      case EVENT_ACTION_TYPES.UPDATE:
        handleUpdate(incomingComment as IComment)
        break
      case EVENT_ACTION_TYPES.DELETE:
        handleDelete(incomingComment)
        break
      default:
        break
    }
  }

  useEffect(() => {
    EventEmitter.subscribe('ENTITY_UPDATE', handleCommentEvents)
    return () => {
      EventEmitter.unsubscribe('ENTITY_UPDATE', handleCommentEvents)
    }
  }, [])

  const renderComment = (comment: IComment) => (
    <ResourceCommentItem
      comment={comment}
      key={comment.id}
      onDelete={handleDelete}
      onUpdate={handleUpdate}
      resourceId={resourceId}
      resourceModule={resourceModule}
    />
  )

  const flatComments = useMemo(() => {
    return comments?.pages.flatMap((page) => page.data) || []
  }, [comments])

  const renderContent = () => {
    if (isLoadingFromDB && !flatComments.length) return <PostCommentLoader />
    if (!flatComments.length)
      return (
        <EmptyState
          customIcon={<Chats size={60} className='text-gray7' />}
          title={t('comments.messages.empty')}
        />
      )
    return (
      <div className='flex flex-col gap-4'>
        {sortArrayOfObjects(
          removeDuplicatesInArray(flatComments, 'id'),
          'pinned'
        ).map(renderComment)}
        <InfiniteScroll
          count={flatComments.length}
          showIf={Boolean(flatComments.length)}
          canFetchMore={hasNextPage}
          onIntersect={() => fetchNextPage()}
          loaderComponent={<PostCommentLoader />}
        />
      </div>
    )
  }

  return (
    <div className='mb-7 space-y-4'>
      {userProfile ? (
        <ResourceCommentForm
          resourceId={resourceId}
          resourceModule={resourceModule}
          onNewComment={handleNewComment}
          editorTextOnlyMenu={true}
        />
      ) : null}
      {renderContent()}
    </div>
  )
}
